/**
 * A person (or a link) as a small round mark. `tone` says their role, always next to words:
 * "owner" (ink blue) | "editor" (plum) | "reader" (green) | "neutral" | "link" (square, for share links).
 * `size`: "sm" 24 | "md" 32 (default) | "lg" 40 | "xl" 56. `src` shows a photo instead of initials.
 * Decorative by default (the name is next to it); pass `label` when it stands alone.
 * (cockatrice-ui `src/ui/Avatar.jsx`, ported to hyperscript; PLAN.md §0)
 */
import './css/avatar.css';
import { mergeProps, splitProps } from 'solid-js';
import { component, each, el, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';
import { uiText } from './text.ts';

export type AvatarTone = 'owner' | 'editor' | 'reader' | 'neutral' | 'link';
export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';
export interface AvatarProps extends Attributes {
  /** Name or email; used for initials and the tooltip. */
  readonly name?: string | undefined;
  /** Photo URL. */
  readonly src?: string | undefined;
  /** Role colour. Default "neutral". */
  readonly tone?: AvatarTone | undefined;
  /** 24, 32, 40 or 56px. Default "md". */
  readonly size?: AvatarSize | undefined;
  /** Accessible name when the avatar stands alone. Otherwise decorative. */
  readonly label?: string | undefined;
  /** Custom content instead of initials (an icon). */
  readonly children?: Child;
  /** Extra classes. */
  readonly class?: string | undefined;
}
export interface AvatarPerson {
  /** Name. */
  readonly name: string;
  /** Role colour. */
  readonly tone?: AvatarTone | undefined;
  /** Photo URL. */
  readonly src?: string | undefined;
}
export interface AvatarGroupProps extends Attributes {
  /** People in order. */
  readonly people: readonly AvatarPerson[];
  /** How many to show before "+n". Default 4. */
  readonly max?: number | undefined;
  /** Size of each avatar. */
  readonly size?: AvatarSize | undefined;
  /** Overrides the generated "A, B and 3 more". */
  readonly label?: string | undefined;
  /** Extra classes. */
  readonly class?: string | undefined;
}

/** "Kari Nilsen" → "KN", "a.berg@example.com" → "AB", "K. Nilsen" → "KN". */
export function initials(name = ''): string {
  const base = name.includes('@') ? name.split('@', 1).join('').replace(/[._-]+/g, ' ') : name;
  const parts = base
    .replace(/[^\p{L}\p{N} ]+/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const first = parts[0];
  if (first === undefined) return '?';
  const last = parts.length > 1 ? parts.slice(-1).join('') : '';
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

export default function Avatar(props: AvatarProps): Child {
  const [local, rest] = splitProps(props, ['class', 'name', 'src', 'tone', 'size', 'label', 'children']);
  const labelled = (): boolean => local.label !== undefined && local.label !== '';
  return el(
    'span',
    mergeProps({
      class: () => ['ui-avatar', `ui-avatar--${local.tone ?? 'neutral'}`, `ui-avatar--${local.size ?? 'md'}`, local.class].filter(Boolean).join(' '),
      role: () => (labelled() ? 'img' : undefined),
      'aria-label': () => local.label,
      'aria-hidden': () => (labelled() ? undefined : 'true'),
      title: () => (labelled() ? undefined : local.name),
    }, rest),
    when(
      () => local.src !== undefined && local.src !== '',
      () => el('img', { class: 'ui-avatar__img', src: () => local.src, alt: '' }),
      () => () => local.children ?? initials(local.name),
    ),
  );
}

/**
 * Overlapping avatars with a "+3" overflow. people: [{ name, tone?, src? }]. `max` (default 4).
 * The group has one label for screen readers: "Shared with Kari Nilsen, Anna Berg and 3 more".
 */
export function AvatarGroup(props: AvatarGroupProps): Child {
  const [local, rest] = splitProps(props, ['class', 'people', 'max', 'size', 'label']);
  const max = (): number => local.max ?? 4;
  const shown = (): readonly AvatarPerson[] => local.people.slice(0, max());
  const extra = (): number => Math.max(0, local.people.length - max());
  const auto = (): string => {
    const names = shown().map((p) => p.name);
    if (extra() > 0) return uiText()('ui.avatar-group.more', { names: names.join(', '), count: extra() });
    if (names.length < 2) return names.join('');
    return uiText()('ui.avatar-group.and', { names: names.slice(0, -1).join(', '), last: names.slice(-1).join('') });
  };
  return el(
    'span',
    mergeProps({
      class: () => ['ui-avatar-group', `ui-avatar-group--${local.size ?? 'md'}`, local.class].filter(Boolean).join(' '),
      role: 'img',
      'aria-label': () => local.label ?? auto(),
    }, rest),
    each(shown, (p) =>
      component(Avatar, {
        get name() {
          return p.name;
        },
        get tone() {
          return p.tone;
        },
        get src() {
          return p.src;
        },
        get size() {
          return local.size;
        },
      }),
    ),
    when(
      () => extra() > 0,
      () => el('span', { class: () => `ui-avatar ui-avatar--neutral ui-avatar--${local.size ?? 'md'} ui-avatar--more`, 'aria-hidden': 'true' }, '+', extra),
    ),
  );
}
